import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  AddReviewLineVars,
  PriceReviewDetail,
  PriceReviewsResponse,
  ReviewLinePatch,
  ReviewPatch,
  ReviewStatus,
} from './reviewTypes'

const ROOT = '/api/customer-price-reviews'

export function usePriceReviews(filters: { partnerId?: number | null; status?: ReviewStatus | null }) {
  const params = new URLSearchParams()
  if (filters.partnerId) params.set('partner_id', String(filters.partnerId))
  if (filters.status) params.set('status', filters.status)
  const qs = params.toString()
  return useQuery({
    queryKey: ['price-reviews', 'list', filters.partnerId ?? null, filters.status ?? null],
    queryFn: () => jsonFetch<PriceReviewsResponse>(`${ROOT}${qs ? `?${qs}` : ''}`),
    staleTime: 10_000,
  })
}

export function usePriceReview(reviewId: string | null) {
  return useQuery({
    queryKey: ['price-reviews', 'detail', reviewId],
    queryFn: () => jsonFetch<PriceReviewDetail>(`${ROOT}/${reviewId}`),
    enabled: reviewId !== null,
  })
}

/** Reviews waiting for an admin. The endpoint is admin-only, so callers gate on that. */
export function usePendingReviewCount(enabled: boolean) {
  return useQuery({
    queryKey: ['price-reviews', 'pending-count'],
    queryFn: () => jsonFetch<{ count: number }>(`${ROOT}/pending-count`),
    enabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

export function useCreateReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { partner_id: number; effective_date?: string | null }) =>
      jsonFetch<PriceReviewDetail>(ROOT, { method: 'POST', body: vars }),
    onSuccess: (data) => {
      qc.setQueryData(['price-reviews', 'detail', data.review.id], data)
      void qc.invalidateQueries({ queryKey: ['price-reviews', 'list'] })
    },
  })
}

/** Every mutation answers with the full detail, so the cache is replaced rather than refetched. */
function useDetailMutation<V>(fn: (vars: V) => Promise<PriceReviewDetail>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(['price-reviews', 'detail', data.review.id], data)
      void qc.invalidateQueries({ queryKey: ['price-reviews', 'list'] })
      void qc.invalidateQueries({ queryKey: ['price-reviews', 'pending-count'] })
    },
  })
}

export function usePatchReview() {
  return useDetailMutation((v: { reviewId: string; changes: ReviewPatch }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}`, { method: 'PATCH', body: v.changes }),
  )
}

export function usePatchReviewLine() {
  return useDetailMutation((v: { reviewId: string; lineId: number; changes: ReviewLinePatch }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/lines/${v.lineId}`, {
      method: 'PATCH',
      body: v.changes,
    }),
  )
}

export function useAddReviewLine() {
  return useDetailMutation((v: { reviewId: string; line: AddReviewLineVars }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/lines`, { method: 'POST', body: v.line }),
  )
}

export function useRemoveReviewLine() {
  return useDetailMutation((v: { reviewId: string; lineId: number }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/lines/${v.lineId}`, { method: 'DELETE' }),
  )
}

function useAction(path: string) {
  return useDetailMutation((reviewId: string) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${reviewId}/${path}`, { method: 'POST' }),
  )
}

export const useRefreshReview = () => useAction('refresh')
export const useSubmitReview = () => useAction('submit')
export const useCancelReview = () => useAction('cancel')
export const useRetryPush = () => useAction('push')

export function useReopenReview() {
  return useDetailMutation((v: { reviewId: string; note?: string }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/reopen`, { method: 'POST', body: { note: v.note ?? null } }),
  )
}

export function useRejectReview() {
  return useDetailMutation((v: { reviewId: string; reason: string }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/reject`, { method: 'POST', body: { reason: v.reason } }),
  )
}

/** Approve and push in one step; the response carries `push` with any per-line failures. */
export function useApproveReview() {
  return useDetailMutation((v: { reviewId: string; effective_date: string }) =>
    jsonFetch<PriceReviewDetail>(`${ROOT}/${v.reviewId}/approve`, {
      method: 'POST',
      body: { effective_date: v.effective_date },
    }),
  )
}

export function useCreateLetterFromReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reviewId: string) =>
      jsonFetch<{ letter_id: string; created: boolean }>(`${ROOT}/${reviewId}/letter`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['price-reviews'] })
      void qc.invalidateQueries({ queryKey: ['price-letters', 'list'] })
    },
  })
}
